/** /orders
 *
 * Ops-wide order search across tenants, driven by the reusable QuerySearch
 * query-builder widget (same contract as /bookings/, over the Order model). The
 * serialized query lives in `?q=`; a tenant picker (`?tenant=12,845`, one or
 * several operators) locks the query's tenant and injects `tour_operator_id`.
 * Server-side pagination + sorting; all column filtering is delegated to the
 * widget (grid `disableColumnFilter`).
 *
 * Booking-level query namespaces (tour/date/state/…) offer an any/all `*` scope
 * modifier via the widget — `any` = the order has ≥1 matching booking, `all` =
 * every booking matches.
 */
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowParams,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as ordersApi from '@/api/orders';
import type { BookingTag } from '@/api/bookings';
import type { OrderListItem } from '@/api/orders';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { BatchSelection } from '@/api/batch';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { deleteBatchAction, type BatchActionDef, type BatchRefusals } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { countLabelDisplayedRows } from '@/components/DataGrid/CountTotalLabel';
import { useCountMode } from '@/components/DataGrid/useCountMode';
import { useGridCountMode } from '@/hooks/useUserPreferences';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { type TimestampsReplaceFn } from '@/components/DataGrid/TimestampsCell';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { EmitEventDialog } from '@/features/emit-events/EmitEventDialog';
import { OrderSummary } from '@/features/orders/overview/OrderSummary';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  parse,
  resolveConditionOperator,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { formatTenantIds } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { buildColumns } from './ordersColumns';
import { buildOrderNamespaces } from './ordersNamespaces';

const DEFAULT_PAGE_SIZE = 50;

const NO_TENANTS: TenantPickerValue[] = [];

const DEFAULT_HIDDEN = [
  'supplier_ref',
  'reseller_ref',
  'custom_id',
  'integration',
  'price',
  'start_datetime',
  'end_datetime',
  // The combined Timestamps column is the visible default; the raw sortable
  // Created/Updated columns are opt-in.
  'created_ts',
  'updated_ts',
  'active',
];

/** Presets store the orders filter state as the raw `?q=` DSL text — it IS the
 *  canonical form (round-trips through parse/serialize), same as bookings. */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q: q } : ({} as Record<string, string>);
  },
};

/** Drop every `tenant` condition from a tree (the picker owns that filter). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && n.namespace === 'tenant') {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        // A group that collapsed to one child is no longer canonical — inline it.
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

export function OrdersPage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildOrderNamespaces(), []);

  // Effective tenants: URL ?tenant= > console scope (src/scope/usePageTenant.ts),
  // a list (`?tenant=12,845`) — the page takes the whole scope.
  const pageTenant = usePageTenant({ multiple: true });
  // Drives the preset defaults' tenant COLUMN only (shown unless exactly one
  // tenant is named); the filtering value is resolved below, once ?q= has been
  // parsed and scope suppression is known.
  const defaultShowTenant = pageTenant.ids.length !== 1;

  const presets = useGridPresets({
    grid: 'orders',
    // The tenant column exists unless exactly one tenant is picked;
    // reconciliation in the hook tolerates it appearing/disappearing.
    defaultOrder: useMemo(
      () => buildColumns({ showTenant: defaultShowTenant }).map((c) => c.field),
      [defaultShowTenant],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  // Parse the URL query into a tree (tolerant — hand-edited URLs are valid).
  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  // Surface parse diagnostics once per distinct query (hand-edited-URL case).
  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // A `tenant=` condition in ?q= — typed, or carried by a loaded preset —
  // already names a tenant. The ambient console scope then stands down rather
  // than AND-ing a second, conflicting tenant onto the query. An explicitly
  // PICKED tenant still wins and strips those conditions (effect below).
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const tenants = pageTenant.fromScope && queryNamesTenant ? NO_TENANTS : pageTenant.values;
  /** What the API's tour_operator_id param carries: `'12,845'` (D1). */
  const tenantApiValue = formatTenantIds(tenants.map((t) => t.id)) ?? undefined;

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  // Reset to page 0 whenever the query or tenant changes.
  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, pageTenant.raw]);

  // When a tenant is picked and the tree still carries tenant conditions,
  // strip them and rewrite ?q= (the picker is authoritative for tenant).
  useEffect(() => {
    // Never for a scope-derived tenant: the console scope must not rewrite the
    // user's (or a preset's) query — that would also diverge a loaded preset.
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      // Editing the query diverges a loaded preset into custom mode.
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  const onTenantChange = pageTenant.setTenants;

  const lockedFilters = tenantApiValue
    ? { tenant: { operator: 'iexact', value: tenantApiValue } }
    : undefined;
  // The suggestion endpoints here are the bookings autocompletes, which take the list.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // Row-total mode, part 1: the persisted override. Read before the query
  // because it belongs in the query key — switching modes must re-issue the
  // request. Part 2 (useCountMode) needs the response and comes after.
  const countPref = useGridCountMode('orders');

  // The filter identity of the current query, shared by the list call and the
  // count-only call so the total can never describe a different set than the
  // rows on screen.
  const countOnlyParams = useMemo(
    () => ({
      ordering,
      ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
      filters: toWireAst(tree),
    }),
    [ordering, tenantApiValue, tree],
  );

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'orders' });
  const query = useQuery({
    queryKey: [
      'orders',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      q,
      extendedTimeout,
      countPref.mode,
    ],
    queryFn: () =>
      ordersApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
        filters: toWireAst(tree),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        // Only when the user overrode the mode; omitting it keeps the backend's
        // own (deferred) default, which is what makes a cold load free.
        ...(countPref.mode ? { count_mode: countPref.mode } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    // While ?preset= is set but its settings haven't loaded, the effective
    // filters/ordering aren't known yet — don't fire a default-state fetch.
    enabled: !presets.blocking,
  });

  // -------------------------------------------------------------------------
  // Batch operations (delete). This grid is a raw DataGridPro (not
  // ResourceDataGrid), so the batch layer is wired here by hand, mirroring
  // BookingsPage: controlled row selection + keepNonExistentRowsSelected, an
  // all-matching mode, a SelectAllBanner, the BatchActionsMenu (via the toolbar
  // slot), and useBatchAction('orders').
  // -------------------------------------------------------------------------
  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  // Reset the all-matching flag whenever the underlying query changes (the
  // "matching" set is no longer what the user opted into).
  const queryIdentity = useMemo(
    () => JSON.stringify({ q, tenant: tenantApiValue, ordering, pageSize: pagination.pageSize }),
    [q, tenantApiValue, ordering, pagination.pageSize],
  );

  // Row totals. This grid defers its count server-side, so `count` arrives as
  // -1 and the pager renders "Show total" instead of a number; MUI paginates on
  // `paginationMeta.hasNextPage`. See components/DataGrid/useCountMode.ts.
  const count = useCountMode({
    pref: countPref,
    queryIdentity,
    response: query.data,
    countOnly: () => ordersApi.count(countOnlyParams),
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
    ...(query.data ? { rowsOnPage: query.data.results.length } : {}),
    onResetPage: () => setPagination((p) => ({ ...p, page: 0 })),
  });
  const rowCount = count.rowCount;
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  const selectedIds = useMemo(
    () => selectionModel.map((i) => Number(i)).filter(Number.isFinite),
    [selectionModel],
  );
  const selectedCount = allMatching ? (count.total ?? selectedIds.length) : selectedIds.length;

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  // The list query string the server must re-run for all_matching — the SAME
  // params ordersApi.list sends (ordering + tour_operator_id + filters), so the
  // batch resolves the identical set the grid shows.
  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    if (tenantApiValue) usp.set('tour_operator_id', tenantApiValue);
    const wire = toWireAst(tree);
    if (wire.length > 0) usp.set('filters', JSON.stringify(wire));
    return usp.toString();
  }, [ordering, tenantApiValue, tree]);

  const batchAction = useBatchAction({
    resource: 'orders',
    queryKey: ['orders'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? filterQuery }),
    [batchAction, filterQuery],
  );

  // Declared unconditionally (batch-gating lesson, 2026-07-24): each action
  // carries its own `permission`; the menu filters, the backend re-checks. A
  // page-level `canManage` gate would hide the checkbox column from operators
  // who hold only another batch action's permission.
  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      deleteBatchAction('console_orders_manage'),
      {
        key: 'emit_event',
        label: 'Emit event',
        action: 'emit_event',
        // Ops Admin only: sends real notifications to guests in bulk.
        permission: 'console_events_emit',
        dialog: (ctx) => <EmitEventDialog ctx={ctx} grid="orders" />,
      },
    ],
    [],
  );

  const batchEnabled = batchActions.length > 0;

  // Timestamps ±delta presets REPLACE the single top-level created|updated
  // range condition (mirrors the Bookings grid). Both are order-level
  // namespaces that always accept a range, so no operator coercion is needed.
  const replaceFilter = useCallback<TimestampsReplaceFn>(
    (ns, cond) => {
      const kept = tree.filter((n) => !(n.kind === 'cond' && n.namespace === ns));
      if (cond) {
        setQuery([
          ...kept,
          { kind: 'cond', namespace: ns, operator: cond.operator, value: cond.value },
        ]);
      } else if (kept.length !== tree.length) {
        setQuery(kept);
      }
    },
    [tree, setQuery],
  );

  // Tags column chips toggle an `order_tag` term (the ORDER's own tags, CP-8095).
  // Routed through resolveConditionOperator like every other cell action so a
  // chip can never emit an operator the backend rejects.
  const isOrderTagCond = useCallback(
    (n: FilterNode, value: string) =>
      n.kind === 'cond' &&
      n.namespace === 'order_tag' &&
      typeof n.value === 'string' &&
      n.value.toLowerCase() === value.toLowerCase(),
    [],
  );

  const isTagActive = useCallback(
    (tag: BookingTag) => tree.some((n) => isOrderTagCond(n, tag.name)),
    [tree, isOrderTagCond],
  );

  const onToggleTag = useCallback(
    (tag: BookingTag) => {
      const idx = tree.findIndex((n) => isOrderTagCond(n, tag.name));
      if (idx >= 0) {
        setQuery(tree.filter((_, i) => i !== idx));
        return;
      }
      const operator = resolveConditionOperator(namespaces.order_tag?.operators, 'iexact');
      if (operator === null) {
        snackbar.show('Filtering by "order_tag" is not supported here.', 'warning');
        return;
      }
      setQuery([...tree, { kind: 'cond', namespace: 'order_tag', operator, value: tag.name }]);
    },
    [tree, setQuery, namespaces, snackbar, isOrderTagCond],
  );

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({
          showTenant: tenants.length !== 1,
          onReplaceFilter: replaceFilter,
          onToggleTag,
          isTagActive,
        }),
        presets.orderedFields,
      ),
    [tenants.length, replaceFilter, onToggleTag, isTagActive, presets.orderedFields],
  );

  // Master-detail: expanding a row shows the read-only Order summary inline
  // (same component as the /orders/:id Overview tab, with its own header).
  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<OrderListItem>) => (
      <Box sx={{ p: 2 }}>
        <OrderSummary orderId={row.id} />
      </Box>
    ),
    [],
  );
  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="orders-grid"
        title="Orders"
        subtitle="Search orders across every tenant with a tokenized query."
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <TenantPicker multiple value={tenants} onChange={onTenantChange} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(lockedFilters ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder='Search orders…  e.g. order_state=confirmed & tour=*"City Walk"'
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}

      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<OrderListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={rowCount}
          {...(count.paginationMeta ? { paginationMeta: count.paginationMeta } : {})}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          getRowHeight={() => 'auto'}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          // DataGridPro does NOT paginate by default (unlike the community
          // grid); without `pagination` it renders a single page with no pager
          // footer, so page/page-size controls never appear.
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          checkboxSelection={batchEnabled}
          disableRowSelectionOnClick
          disableColumnFilter
          {...(batchEnabled
            ? {
                rowSelectionModel: selectionModel,
                onRowSelectionModelChange: handleSelectionChange,
                keepNonExistentRowsSelected: true,
              }
            : {})}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch ? 'No orders match this search.' : 'Search to find orders.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            pagination: {
              labelDisplayedRows: countLabelDisplayedRows({
                state: count.state,
                total: count.total,
                canRequest: true,
                onRequest: count.requestTotal,
              }),
            },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
              hideFilterButton: true,
              exportFileName: 'orders',
              presets: presets.toolbar,
              countMode: { mode: count.mode, setMode: count.setMode },
              ...(batchEnabled
                ? {
                    batch: {
                      actions: batchActions,
                      ids: selectedIds,
                      allMatching,
                      count: selectedCount,
                      matched: count.total,
                      filterQuery,
                      runStandard,
                      clearSelection,
                      onSelectAllMatching: () => setAllMatching(true),
                      isPending: batchAction.isPending,
                    },
                  }
                : {}),
            } as GridToolbarProps,
          }}
        />
      )}

      {query.data?.count_is_capped && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          10,000+ results — refine your search to see more precise counts.
        </Typography>
      )}
    </Box>
  );
}
